/**
 * /ninety/issues — My Day · Issues.
 *
 * VISUAL REFERENCE build for the development team: reads and writes the fake
 * in-memory store in ~/components/myday/store.ts. Search, severity filter,
 * sort, team chips, "Show closed", close, create, edit and delete all work
 * in-session and reset on reload.
 *
 * "Sort: Person assigned" groups the list under a grey header per person
 * (first name, "Unassigned" last); the other sorts render a flat list.
 */
import { Fragment, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { cn } from '~/lib/cn';
import {
  useMyDay, issuesApi, personById, ME_ID, TEAMS, type Issue, type Severity,
} from '~/components/myday/store';
import {
  Panel, PanelToolbar, Avatar, SearchBox, ToolbarSelect, ClearButton, TeamChips, Switch,
  NewButton, ToolbarActions, PagerFooter, RoundCheck, EmptyState, FakeDataNote, IconIssue,
} from '~/components/myday/ui';
import { IssueDialog, SeverityPill } from '~/components/myday/issues/IssueDialog';

export const Route = createFileRoute('/_app/ninety/issues')({
  component: IssuesTab,
});

type SeverityFilter = 'any' | Severity;
type SortKey = 'person' | 'severity' | 'newest' | 'title';

const SEVERITY_OPTIONS: { value: SeverityFilter; label: string }[] = [
  { value: 'any', label: 'Any severity' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'person', label: 'Sort: Person assigned' },
  { value: 'severity', label: 'Sort: Severity' },
  { value: 'newest', label: 'Sort: Newest' },
  { value: 'title', label: 'Sort: Title' },
];

const SEV_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
const UNASSIGNED = 'Unassigned';

function groupName(i: Issue): string {
  return personById(i.assigneeId)?.name.split(' ')[0] ?? UNASSIGNED;
}

function IssuesTab() {
  const issues = useMyDay((s) => s.issues);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState<SeverityFilter>('any');
  const [sort, setSort] = useState<SortKey>('person');
  const [team, setTeam] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  // undefined = closed, null = new, Issue = editing
  const [editing, setEditing] = useState<Issue | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = issues.filter((i) => {
      if (severity !== 'any' && i.severity !== severity) return false;
      if (team && i.team !== team) return false;
      if (!showClosed && i.closed) return false;
      if (q && !`${i.title} ${i.detail}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const newest = (a: Issue, b: Issue) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    return [...rows].sort((a, b) => {
      switch (sort) {
        case 'severity': return SEV_RANK[a.severity] - SEV_RANK[b.severity] || newest(a, b);
        case 'newest': return newest(a, b);
        case 'title': return a.title.localeCompare(b.title);
        default: {
          const ga = groupName(a); const gb = groupName(b);
          if (ga !== gb) {
            if (ga === UNASSIGNED) return 1;
            if (gb === UNASSIGNED) return -1;
            return ga.localeCompare(gb);
          }
          return SEV_RANK[a.severity] - SEV_RANK[b.severity] || newest(a, b);
        }
      }
    });
  }, [issues, search, severity, sort, team, showClosed]);

  const visible = filtered.slice(0, pageSize);
  const grouped = sort === 'person';

  const clear = () => {
    setSearch(''); setSeverity('any'); setSort('person'); setTeam(null); setShowClosed(false);
  };

  return (
    <div className="px-4 sm:px-6 py-4">
      <Panel>
        <PanelToolbar>
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={search} onChange={setSearch} placeholder="Search issues..." />
            <ToolbarSelect value={severity} onChange={setSeverity} options={SEVERITY_OPTIONS} />
            <ToolbarSelect value={sort} onChange={setSort} options={SORT_OPTIONS} className="min-w-[190px]" />
            <ClearButton onClick={clear} />
          </div>
          <TeamChips value={team} onChange={setTeam} count={filtered.length} />
          <ToolbarActions>
            <Switch checked={showClosed} onChange={setShowClosed} label="Show closed" />
            <NewButton onClick={() => setEditing(null)} />
          </ToolbarActions>
        </PanelToolbar>

        <div className="overflow-x-auto">
          <div className="min-w-[520px]">
            {visible.length === 0 ? (
              <EmptyState icon={<IconIssue className="w-8 h-8" />} title="No issues"
                body="Nothing matches these filters. Clear them or raise a new issue." />
            ) : (
              visible.map((i, idx) => {
                const header = grouped && (idx === 0 || groupName(visible[idx - 1]) !== groupName(i));
                return (
                  <Fragment key={i.id}>
                    {header && (
                      <div className="px-4 py-1.5 bg-slate-100 border-b border-slate-200 text-[13px] font-semibold text-slate-700">
                        {groupName(i)}
                      </div>
                    )}
                    <div role="button" tabIndex={0}
                      onClick={() => setEditing(i)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setEditing(i); }}
                      className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50">
                      <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                        <RoundCheck checked={i.closed} label={i.closed ? 'Reopen issue' : 'Close issue'}
                          onChange={() => issuesApi.update(i.id, { closed: !i.closed })} />
                      </span>
                      <span className={cn('flex-1 min-w-0 truncate text-[15px]',
                        i.closed ? 'line-through text-slate-400' : 'text-slate-900')}>
                        {i.title}
                      </span>
                      <SeverityPill severity={i.severity} />
                      <Avatar person={personById(i.assigneeId)} />
                    </div>
                  </Fragment>
                );
              })
            )}
          </div>
        </div>

        <PagerFooter pageSize={pageSize} onPageSize={setPageSize} shown={visible.length} total={filtered.length} />
      </Panel>
      <FakeDataNote />

      {editing !== undefined && (
        <IssueDialog issue={editing} onClose={() => setEditing(undefined)}
          defaults={{ team: team ?? TEAMS[0], assigneeId: ME_ID }} />
      )}
    </div>
  );
}
